import { z } from 'zod'

/**
 * Postcode -> tambon lookup, no map anywhere in the product (SPEC).
 *
 * The file is stored in the compact tuple form because the full Thai dataset is
 * ~50k rows and the buyer's phone downloads it: `{ "10110": [["คลองตัน","วัฒนา","กรุงเทพมหานคร"]] }`
 * is roughly a third of the equivalent object-per-row JSON.
 *
 * Fetched at runtime, so it is a trust boundary — hence the Zod parse.
 */
export const THAI_ADDRESS_URL = '/data/thai-address.json'

export const CompactAddressRowSchema = z.tuple([
  z.string().min(1), // subdistrict (ตำบล / แขวง)
  z.string().min(1), // district (อำเภอ / เขต)
  z.string().min(1), // province (จังหวัด)
])

export const CompactAddressDataSchema = z.object({
  /** Free-text provenance so nobody has to guess which dataset shipped. */
  source: z.string().default(''),
  updatedAt: z.string().default(''),
  byPostcode: z.record(z.string().regex(/^\d{5}$/), z.array(CompactAddressRowSchema).min(1)),
})
export type CompactAddressData = z.infer<typeof CompactAddressDataSchema>

export type AddressArea = {
  postcode: string
  subdistrict: string
  district: string
  province: string
}

export type ThaiAddressIndex = {
  source: string
  updatedAt: string
  byPostcode: Map<string, AddressArea[]>
}

export const parseAddressData = (raw: unknown): ThaiAddressIndex => {
  const data = CompactAddressDataSchema.parse(raw)
  const byPostcode = new Map<string, AddressArea[]>()
  for (const [postcode, rows] of Object.entries(data.byPostcode)) {
    byPostcode.set(
      postcode,
      rows.map(([subdistrict, district, province]) => ({ postcode, subdistrict, district, province })),
    )
  }
  return { source: data.source, updatedAt: data.updatedAt, byPostcode }
}

/** Empty array for an unknown postcode — the form then lets the buyer type it in. */
export const areasForPostcode = (index: ThaiAddressIndex, postcode: string): AddressArea[] =>
  index.byPostcode.get(postcode.trim()) ?? []
